import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { Button, Input, TextArea, FormField, Alert } from '../ui';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface DreamFormProps {
  isOpen: boolean;
  onClose: () => void;
  onDreamCreated: (dream: any) => void;
  initialDream?: any;
  isEditMode?: boolean;
}

const initialFormData = {
  title: '',
  description: '',
  inspiration: '',
  driver: '',
  is_active: true,
};

const DreamForm: React.FC<DreamFormProps> = ({
  isOpen,
  onClose,
  onDreamCreated,
  initialDream = null,
  isEditMode = false,
}) => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && initialDream) {
        setFormData({
          title: initialDream.title || '',
          description: initialDream.description || '',
          inspiration: initialDream.inspiration || '',
          driver: initialDream.driver || '',
          is_active: initialDream.is_active !== undefined ? initialDream.is_active : true,
        });
      } else {
        setFormData(initialFormData);
      }
    }
    if (!isOpen) {
      setTimeout(() => {
        setSubmitError(null);
        setSubmitSuccess(false);
      }, 300);
    }
  }, [isOpen, isEditMode, initialDream]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    const payload: any = { title: formData.title, is_active: formData.is_active };
    if (formData.description) payload.description = formData.description;
    if (formData.inspiration) payload.inspiration = formData.inspiration;
    if (formData.driver) payload.driver = formData.driver;

    try {
      let response;
      if (isEditMode && initialDream) {
        response = await apiClient.patch(`/api/dreams/${initialDream.id}/`, payload);
      } else {
        response = await apiClient.post('/api/dreams/', payload);
      }

      if (response.status >= 200 && response.status < 300) {
        setSubmitSuccess(true);
        onDreamCreated(response.data);
        if (!isEditMode) {
          setFormData(initialFormData);
        }
        setTimeout(() => { onClose(); }, 1500);
      } else {
        throw new Error(`Failed to ${isEditMode ? 'update' : 'create'} dream (${response.status})`);
      }
    } catch (err: any) {
      const errorMsg = err.message || `Failed to ${isEditMode ? 'update' : 'create'} dream.`;
      setSubmitError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    if (!isSubmitting) { onClose(); }
  }, [isSubmitting, onClose]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
          }} />
        </Transition.Child>

        <div style={{
          position: 'fixed',
          inset: 0,
          overflowY: 'auto',
        }}>
          <div style={{
            display: 'flex',
            minHeight: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING[16],
            textAlign: 'center',
          }}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel style={{
                width: '100%',
                maxWidth: '28rem',
                transform: 'none',
                overflow: 'hidden',
                borderRadius: RADIUS,
                backgroundColor: COLORS.bg.panel,
                padding: SPACING[24],
                textAlign: 'left',
                verticalAlign: 'middle',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                transition: 'all 150ms ease-in-out',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: SPACING[16],
                }}>
                  <Dialog.Title as="h3" style={{
                    fontSize: TYPOGRAPHY.size['2xl'],
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.primary,
                  }}>
                    {isEditMode ? 'Edit Dream' : 'Create New Dream'}
                  </Dialog.Title>
                  <button
                    type="button"
                    style={{
                      color: COLORS.text.tertiary,
                      opacity: isSubmitting ? 0.5 : 1,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                    }}
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    <XMarkIcon style={{ height: '24px', width: '24px' }} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                  <FormField label="Title" htmlFor="title" required>
                    <Input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                    />
                  </FormField>

                  <FormField label="Description" htmlFor="description">
                    <TextArea
                      id="description"
                      name="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange}
                    />
                  </FormField>

                  <FormField label="Inspiration" htmlFor="inspiration">
                    <TextArea
                      id="inspiration"
                      name="inspiration"
                      rows={2}
                      value={formData.inspiration}
                      onChange={handleInputChange}
                    />
                  </FormField>

                  <FormField label="Driver" htmlFor="driver">
                    <TextArea
                      id="driver"
                      name="driver"
                      rows={2}
                      value={formData.driver}
                      onChange={handleInputChange}
                    />
                  </FormField>

                  <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                    <input
                      type="checkbox"
                      id="is_active"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleCheckboxChange}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <label
                      htmlFor="is_active"
                      style={{
                        fontSize: TYPOGRAPHY.body.sizes.default,
                        color: COLORS.text.secondary,
                        cursor: 'pointer',
                      }}
                    >
                      Active
                    </label>
                  </div>

                  <div style={{ marginTop: SPACING[24], display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
                    {submitSuccess && (
                      <Alert variant="success">
                        Dream {isEditMode ? 'updated' : 'created'}!
                      </Alert>
                    )}
                    {submitError && (
                      <Alert variant="error">
                        {submitError}
                      </Alert>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: SPACING[12] }}>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        isLoading={isSubmitting}
                        loadingText={isEditMode ? 'Updating...' : 'Creating...'}
                      >
                        {isEditMode ? 'Update Dream' : 'Create Dream'}
                      </Button>
                    </div>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DreamForm;
